import { makeStyles } from '@material-ui/styles'
import { maintenanceModeDoc } from 'app/docLinks'
import StatsCell from 'app/plugins/infrastructure/components/common/cells/StatsCell'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { listImages } from 'openstack/components/images/actions'
import { vtpmImagesSelector } from 'openstack/components/images/selectors'
import { simpleAddVmTag, simpleMigrateVirtualMachine } from 'openstack/components/vms/actions'
import {
  VALID_COLD_MIGRATION_STATES,
  VALID_MIGRATION_STATES,
} from 'openstack/components/vms/MigrateVirtualMachineDialog'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { v4 as uuid } from 'uuid'
import { enableMaintenanceMode, getVmsForHost, listHypervisors, listResmgrHosts } from '../actions'
import { activeRoleHostsByCluster } from '../clusters/selectors'
import { hypervisorsSelector, resmgrHostsSelector, UNASSIGNED_CLUSTER } from '../selectors'
import { IpAddressCellComponent, RoleStatusesCell } from './HostsListPage'

const getBadgeVariant = (status) =>
  ({
    active: 'success',
    paused: 'warning',
    stopped: 'error',
    error: 'error',
    suspended: 'warning',
    building: 'success',
    rescued: 'warning',
  }[status] || 'unknown')

const getBadgeText = (status, pausedPowerState, taskState) => {
  const text =
    {
      active: 'Active',
      paused: 'Paused',
      stopped: 'Stopped',
      error: 'Error',
      suspended: 'Suspended',
      building: 'Building',
      rescued: 'Rescued',
    }[status] || status
  return taskState ? `${text} (${taskState})` : pausedPowerState ? `${text} (paused)` : text
}

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
  migrations: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  migrationHeader: {
    textAlign: 'center',
  },
  migratingVms: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr 1fr 1fr',
    gap: '8px 32px',
    paddingBottom: 16,
  },
  centered: {
    textAlign: 'center',
  },
  blockingVmsTable: {
    display: 'inline-grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
    marginTop: 16,
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  errorIcon: {
    marginRight: 4,
  },
  tabbed: {
    marginTop: 8,
    marginLeft: 16,
    display: 'grid',
    gap: 4,
  },
}))

interface Props {
  rows: any[]
  onClose: (success?: boolean) => void
}

const hostColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'roleStatuses',
    label: 'Role Status',
    render: (value, item) => <RoleStatusesCell value={value} item={item} />,
  },
  {
    key: 'responding',
    label: 'Connection Status',
  },
  {
    id: 'interfaces',
    label: 'IP Address',
    render: (value) => <IpAddressCellComponent value={value} />,
  },
  {
    id: 'clusterName',
    label: 'cluster',
  },
  {
    id: 'usage',
    label: 'Resource Utilization',
    render: (value) => <StatsCell value={value} />,
  },
  {
    id: 'displayRoles',
    label: 'Assigned Roles',
    render: (values) => values.join(', '),
  },
]

export default function EnableMaintenanceModeDialog({ rows: hosts, onClose }: Props) {
  const host = hosts?.[0]
  const classes = useStyles()
  const { history } = useReactRouter()
  const [loadingVms, setLoadingVms] = useState(true)
  const [vmsOnHost, setVmsOnHost] = useState([])
  const [submittingMigrations, setSubmittingMigrations] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)

  const roleHostsByCluster = useSelector(activeRoleHostsByCluster)
  const isLastHypervisor = useMemo(() => {
    const clusterHypervisors = roleHostsByCluster?.[host?.clusterName]?.hypervisor
    return clusterHypervisors?.length === 1 && clusterHypervisors?.[0]?.id === host?.id
  }, [roleHostsByCluster, host])

  const reloadVmsOnHost = useCallback(() => {
    const makeCalls = async () => {
      const vms = await getVmsForHost(host.id)
      setVmsOnHost(vms)
      setLoadingVms(false)
    }
    makeCalls()
  }, [host])

  const allowMaintenance = useMemo(() => {
    if (loadingVms) {
      return false
    }
    return true
    // if restricting maintenance, seems with new wireframes this is
    // not required anymore
    // return !vmsOnHost.some((vm) => ['resized', 'rescued'].includes(vm?.['OS-EXT-STS:vm_state']))
  }, [loadingVms, vmsOnHost])

  const blockingVms = useMemo(() => {
    if (loadingVms) {
      return []
    }

    return vmsOnHost.filter((vm) => ['resized', 'rescued'].includes(vm?.['OS-EXT-STS:vm_state']))
  }, [loadingVms, vmsOnHost])

  const { loading: loadingImages } = useListAction(listImages)
  const vtpmImages = useSelector(vtpmImagesSelector)

  useEffect(() => {
    // Get initial number of VMs on the host to determine
    // if migrations are necessary
    reloadVmsOnHost()
  }, [])

  const defaultParams = {
    selectHost: false,
    skipPoweredOffVms: false,
    host: [],
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { message, loading: loadingResmgrHosts, reload } = useListAction(listResmgrHosts, {
    params,
  })

  const allHosts = useSelector(resmgrHostsSelector)
  const { loading: loadingHypervisors, reload: reloadHypervisors } = useListAction(listHypervisors)
  const hypervisors = useSelector(hypervisorsSelector)

  const validHosts = useMemo(() => {
    if (!allHosts.length || !host || !hypervisors.length) {
      return []
    }
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor?.service?.host]: hypervisor,
      }
    }, {})
    const hostsWithHypervisor = allHosts.map((host) => {
      return {
        ...host,
        hypervisor: hypervisorsByResmgrId[host.id],
      }
    })

    return hostsWithHypervisor.filter((resmgrHost) => {
      return (
        resmgrHost.id !== host.id &&
        resmgrHost.hypervisor?.state === 'up' &&
        resmgrHost.hypervisor?.status === 'enabled' &&
        // If the host is unassigned, then it can be moved to any cluster
        // If the host is assigned to a cluster, then it can only be moved to host with the same cluster or unassigned host
        (host?.clusterName === UNASSIGNED_CLUSTER ||
          resmgrHost?.clusterName === host?.clusterName ||
          resmgrHost?.clusterName === UNASSIGNED_CLUSTER)
      )
    })
  }, [allHosts, host, hypervisors])

  const {
    update: disableHypervisor,
    updating: disablingHypervisor,
    error: errorDisablingHypervisor,
    reset: resetDisableHypervisor,
  } = useUpdateAction(enableMaintenanceMode)

  const handleMaintenanceToggle = useCallback(
    async (migrationId) => {
      const timestamp = Date.now()
      const body = {
        host: host.id,
        binary: 'nova-compute',
        disabled_reason: `${migrationId}:${timestamp}`,
      }
      await disableHypervisor({ id: host.hypervisor.id, body })

      // Close the dialog if no VMs on the host
      if (!vmsOnHost.length) {
        handleClose(true)
      }
    },
    [params, host, vmsOnHost.length],
  )

  const triggerMigrations = useCallback(async () => {
    setSubmittingMigrations(true)

    const migrationId = uuid()

    handleMaintenanceToggle(migrationId)

    const validVms = vmsOnHost.filter((vm) => {
      // Skip active vTPM vms
      if (['active', 'paused'].includes(vm['OS-EXT-STS:vm_state'])) {
        if (vm?.flavor?.extra_specs?.tpm_version) {
          return false
        }
        if (vm?.image?.id) {
          if (vtpmImages.some((image) => image?.id === vm?.image?.id)) {
            return false
          }
        }
      }
      // Skip powered off VMs if flag toggled
      return params.skipPoweredOffVms
        ? ['active', 'paused', 'suspended'].includes(vm['OS-EXT-STS:vm_state'])
        : VALID_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
    })

    const tagPromises = validVms.map((vm) => simpleAddVmTag({ id: vm.id, tag: migrationId }))
    const tagResults = await Promise.allSettled(tagPromises)
    // If any VMs were not tagged properly and they are migrated off the host, then will lose history that they were part of the migration.
    // const failedTagIndexes = tagResults
    //   ?.map((result, idx) => (result.status === 'rejected' ? idx : null))
    //   ?.filter((idx) => idx !== null)
    // const failedTagVms = failedTagIndexes.map((idx) => validVms[idx])
    // Potentially show something regarding the VMs that didn't get tagged

    const promises = validVms.map((vm) => {
      const body = VALID_COLD_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
        ? {
            migrate: {
              host: params.selectHost ? params.host[0]?.id : null,
            },
          }
        : {
            'os-migrateLive': {
              host: params.selectHost ? params.host[0]?.id : null,
              block_migration: 'auto',
            },
          }
      return simpleMigrateVirtualMachine({ id: vm.id, body })
    })
    const results = await Promise.allSettled(promises)
    const acceptedMigrationIndexes = results
      ?.map((result, idx) => (result.status === 'fulfilled' ? idx : null))
      ?.filter((idx) => idx !== null)
    // If we need to show which VMs were successfully triggered in the future
    // const vmsWithMigrationTriggered = acceptedMigrationIndexes.map((idx) => validVms[idx])

    history.push(routeManager.routes.openstack.hostDetails.path({ id: host?.id }))
  }, [params, host, vmsOnHost, vtpmImages, handleMaintenanceToggle])

  const handleClose = (success?: boolean) => {
    resetDisableHypervisor()
    onClose(success)
  }

  return (
    <ModalForm
      title={'Enable Maintenance Mode'}
      onSubmit={vmsOnHost.length ? triggerMigrations : () => handleMaintenanceToggle(uuid())}
      onClose={() => (submittingMigrations ? setOpenConfirmation(true) : handleClose())}
      loading={loadingVms || loadingImages}
      submitting={disablingHypervisor || submittingMigrations}
      error={errorDisablingHypervisor}
      submitTitle={
        vmsOnHost.length ? 'Migrate VMs and Enable Maintenance Mode' : 'Enable Maintenance Mode'
      }
      panel="dialog"
      maxWidth={1200}
      disableSubmit={!allowMaintenance}
      open
      onBackdropClick={() => {
        submittingMigrations ? setOpenConfirmation(true) : handleClose()
      }}
    >
      <ConfirmationDialog
        open={openConfirmation}
        text="Migrations in progress. Are you sure you want to cancel?"
        onCancel={() => setOpenConfirmation(false)}
        onConfirm={handleClose}
      />
      <div className={classes.fields}>
        {allowMaintenance ? (
          <>
            {isLastHypervisor && (
              <Info error>
                <Text variant="body2">
                  <FontAwesomeIcon className={classes.errorIcon}>
                    triangle-exclamation
                  </FontAwesomeIcon>
                  <b>WARNING:</b> You are about to put the only host in this cluster with hypervisor
                  role assigned in maintenance mode. Putting this host in maintenance mode will have
                  following side effects:
                </Text>
                <div className={classes.tabbed}>
                  <Text variant="body2">
                    <b>1.</b> VMs that are eligible for migration will be migrated off the cluster.
                  </Text>
                  <Text variant="body2">
                    <b>2.</b> For remaining VMs, all VM operations on this cluster other than
                    editing VM metadata and migrating the VM off the cluster will fail.
                  </Text>
                </div>
              </Info>
            )}
            <Text variant="body2">
              You are about to enable maintenance mode on <b>{host?.name}</b>.
            </Text>
            <Text variant="body2">
              When in maintenance mode, VMs cannot be created or powered on. Additionally, Private
              Cloud Director will not schedule or migrate any VMs to this host while it is in
              maintenance mode. Refer to{' '}
              <ExternalLink url={maintenanceModeDoc}>documentation</ExternalLink> for details.
            </Text>
            {vmsOnHost?.length ? (
              <>
                <Text variant="body2">
                  Private Cloud Director will disable scheduling, then attempt to migrate all
                  eligible VMs on this host to other hosts, or to the selected host. There are
                  currently <b>{vmsOnHost?.length}</b> VM(s) on this host.
                </Text>
                <Text variant="body2">
                  <b>NOTE:</b> If you have any vTPM VMs, please shut off these VMs prior to
                  migration. Active vTPM VMs detected will be skipped.
                </Text>
                <ToggleSwitchField
                  id="skipPoweredOffVms"
                  label="Skip migration of powered off VMs"
                  value={params.skipPoweredOffVms}
                  onChange={getParamsUpdater('skipPoweredOffVms')}
                  customTooltipInfoBody={
                    <Text variant="body2" className={classes.switchTooltip}>
                      If enabled, powered off (stopped) VMs will not be migrated.
                    </Text>
                  }
                />
                <ToggleSwitchField
                  id="selectHost"
                  label="Designate a specific host to migrate VMs to"
                  value={params.selectHost}
                  onChange={getParamsUpdater('selectHost')}
                  customTooltipInfoBody={
                    <Text variant="body2" className={classes.switchTooltip}>
                      If enabled, select a host to which all active VMs on this host will be
                      migrated to. If left disabled, VMs will be distributed to new hosts
                      automatically.
                    </Text>
                  }
                />
              </>
            ) : (
              <Text variant="body2">
                There are no VMs on this host. Maintenance mode may be enabled, and scheduling will
                be disabled on this host.
              </Text>
            )}
            {params.selectHost && (
              <ListTableField
                id="host"
                data={validHosts}
                loading={loadingResmgrHosts || loadingHypervisors}
                columns={hostColumns}
                onChange={getParamsUpdater('host')}
                value={params.host}
                uniqueIdentifier="id"
                searchTargets={['name']}
                noRowsPerPage
                required
              />
            )}
          </>
        ) : (
          <>
            <Text variant="body2">
              You are about to enable maintenance mode on host <b>{host.name}</b>. Refer to this{' '}
              <ExternalLink url={maintenanceModeDoc}>article</ExternalLink> for more information.
            </Text>
            <Info className={classes.centered} error>
              <Text variant="body2">
                You cannot enable maintenance mode while any VMs are in a <b>resized</b> or{' '}
                <b>rescued</b> state. Please address the affected VMs below.
              </Text>
              <div className={classes.blockingVmsTable}>
                <Text variant="caption1">VM</Text>
                <Text variant="caption1">State</Text>
                {blockingVms.map((vm) => (
                  <>
                    <SimpleLink
                      src={routeManager.routes.openstack.vmDetails.path({
                        id: vm?.id,
                        tab: IVmDetailsPageTabs.Overview,
                      })}
                    >
                      {vm?.name || vm?.id}
                    </SimpleLink>
                    <Text variant="body2">{vm?.['OS-EXT-STS:vm_state']}</Text>
                  </>
                ))}
              </div>
            </Info>
          </>
        )}
      </div>
    </ModalForm>
  )
}
